import {
  Activity,
  AlertTriangle,
  Bot,
  Building2,
  CheckCircle2,
  CreditCard,
  Database,
  FileText,
  Headphones,
  ListChecks,
  MessageSquareText,
  Mic2,
  Play,
  RefreshCcw,
  Settings,
  ShieldCheck,
  UserRound
} from "lucide-react";
import { type ReactNode, useEffect, useMemo, useState } from "react";
import { AuthOrgProvisioningPanel } from "./auth";
import { BillingPanel } from "./billing";
import {
  type AdminAuthSession,
  clearAuthSession,
  getActiveOrgId,
  loadAuthSession,
  saveAuthSession
} from "../lib/access";
import { confirmAdminEmail, loadAdminSession, switchAdminOrg } from "../lib/auth-api";
import { asText, extractList, formatDate, voiceRequest } from "../lib/voice-api";
import {
  DEFAULT_VOICE_ENGINE_ID,
  OUTBOUND_CAMPAIGN_PRICING,
  VOICE_ENGINE_OPTIONS,
  findVoiceEngineOption,
  formatCostRate,
  formatCustomerRate,
  formatMinuteRate,
  type VoiceEngineId,
  type VoiceEngineOption
} from "../lib/voice-pricing";

type Section =
  | "dashboard"
  | "assistant-defaults"
  | "account"
  | "billing"
  | "settings";

type VoiceRecord = Record<string, unknown>;

interface RuntimeHealth extends VoiceRecord {
  status?: string;
  service?: string;
  runtime?: string;
  version?: string;
  database?: string;
  database_migration?: string;
  active_calls?: number;
}

interface AdminData {
  health: RuntimeHealth | null;
  accounts: VoiceRecord[];
}

interface NavItem {
  id: Section;
  label: string;
  group: "Workspace" | "Account";
  icon: typeof Activity;
}

const navItems: NavItem[] = [
  { id: "dashboard", label: "Dashboard", group: "Workspace", icon: Activity },
  { id: "assistant-defaults", label: "Assistant Defaults", group: "Workspace", icon: Bot },
  { id: "account", label: "Account", group: "Account", icon: UserRound },
  { id: "billing", label: "Billing", group: "Account", icon: CreditCard },
  { id: "settings", label: "Settings", group: "Account", icon: Settings }
];

const navGroups: Array<NavItem["group"]> = ["Workspace", "Account"];

const emptyData: AdminData = {
  health: null,
  accounts: []
};

const voiceOptions = [
  {
    id: "ava",
    name: "Ava",
    tone: "Warm, clear, service-focused",
    sample: "Thanks for calling. I can help with bookings, questions, and routing you to the right person."
  },
  {
    id: "miles",
    name: "Miles",
    tone: "Calm, confident, concise",
    sample: "Hi, you have reached the team. Tell me what you need and I will get it handled."
  },
  {
    id: "harper",
    name: "Harper",
    tone: "Bright, friendly, energetic",
    sample: "Hello, thanks for calling. I can check availability, take a message, or connect you now."
  },
  {
    id: "brand",
    name: "Brand Voice",
    tone: "Custom approved voice profile",
    sample: "This is your branded Glimbr Voice assistant, ready to handle customer calls in your house style."
  }
] as const;

type VoiceId = typeof voiceOptions[number]["id"];

interface AssistantDefaultsDraft {
  name: string;
  firstMessage: string;
  systemPrompt: string;
  voiceId: VoiceId;
  language: string;
  engineId: VoiceEngineId;
  recordCalls: boolean;
  saveTranscripts: boolean;
}

const defaultAssistantDefaults: AssistantDefaultsDraft = {
  name: "Front desk assistant",
  firstMessage: "Thanks for calling. How can I help?",
  systemPrompt: "You are a helpful phone assistant. Keep replies natural, concise, and useful.",
  voiceId: "ava",
  language: "en",
  engineId: DEFAULT_VOICE_ENGINE_ID,
  recordCalls: true,
  saveTranscripts: true
};

export function App() {
  const [activeSection, setActiveSection] = useState<Section>("dashboard");
  const [authSession, setAuthSession] = useState<AdminAuthSession | null>(() => loadAuthSession());
  const [authChecking, setAuthChecking] = useState(true);
  const [orgId, setOrgId] = useState(() => getActiveOrgId());
  const [data, setData] = useState<AdminData>(emptyData);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const metrics = useMemo(
    () => {
      const defaults = assistantDefaultsFromAccount(data.accounts[0]);
      const engine = findVoiceEngineOption(defaults.engineId);
      return [
        { label: "Calls now", value: String(data.health?.active_calls ?? 0), tone: "green" },
        { label: "Voice engine", value: engine.tier, tone: "blue" },
        { label: "Inbound rate", value: `${formatCustomerRate(engine.suggestedInboundPrice)}/min`, tone: "amber" },
        { label: "Team members", value: String(data.accounts.length), tone: "neutral" }
      ];
    },
    [data]
  );

  useEffect(() => {
    let cancelled = false;

    async function bootstrapAuth() {
      const url = new URL(window.location.href);
      const token = url.pathname === "/confirm-email" ? url.searchParams.get("token") : null;

      if (token) {
        try {
          const result = await confirmAdminEmail(token);
          if (!cancelled) {
            setNotice(`${result.email} is confirmed. Sign in to continue.`);
          }
        } catch (err) {
          if (!cancelled) {
            setError(err instanceof Error ? err.message : String(err));
          }
        } finally {
          window.history.replaceState({}, "", "/");
        }
      }

      const storedSession = loadAuthSession();
      if (!storedSession) {
        if (!cancelled) setAuthChecking(false);
        return;
      }

      try {
        const nextSession = saveAuthSession(await loadAdminSession(storedSession.sessionKey));
        if (!cancelled) {
          setAuthSession(nextSession);
          setOrgId(nextSession.activeOrg.orgId);
        }
      } catch {
        clearAuthSession();
        if (!cancelled) {
          setAuthSession(null);
        }
      } finally {
        if (!cancelled) setAuthChecking(false);
      }
    }

    void bootstrapAuth();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!authSession) {
      setData(emptyData);
      setLoading(false);
      return;
    }

    setOrgId(authSession.activeOrg.orgId);
    void loadData(authSession.activeOrg.orgId);
  }, [authSession?.sessionKey, authSession?.activeOrg.orgId]);

  async function loadData(nextOrgId = orgId) {
    setLoading(true);
    setError(null);

    const results = await Promise.allSettled([
      voiceRequest<RuntimeHealth>("/health"),
      voiceRequest<unknown>("/account", { orgId: nextOrgId })
    ]);

    const failures = results
      .filter((result): result is PromiseRejectedResult => result.status === "rejected")
      .map((result) => result.reason instanceof Error ? result.reason.message : String(result.reason));

    setData({
      health: valueOrNull<RuntimeHealth>(results[0]),
      accounts: extractList<VoiceRecord>(valueOrEmpty(results[1]), "accounts")
    });

    if (failures.length > 0) {
      setError(failures[0]);
    }

    setLoading(false);
  }

  function handleSessionChange(nextSession: AdminAuthSession | null) {
    setAuthSession(nextSession);
    if (nextSession) {
      setOrgId(nextSession.activeOrg.orgId);
      setNotice(`Signed in to ${nextSession.activeOrg.orgName}`);
    } else {
      setData(emptyData);
      setNotice("Signed out");
    }
  }

  async function switchHeaderOrg(nextOrgId: string) {
    if (!authSession || nextOrgId === authSession.activeOrg.orgId) return;
    setError(null);
    setNotice(null);

    try {
      const nextSession = saveAuthSession(await switchAdminOrg(nextOrgId, authSession.sessionKey));
      setAuthSession(nextSession);
      setOrgId(nextSession.activeOrg.orgId);
      setNotice(`Switched to ${nextSession.activeOrg.orgName}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }

  const ActiveIcon = navItems.find((item) => item.id === activeSection)?.icon || Activity;

  if (authChecking) {
    return (
      <main className="auth-shell">
        <div className="auth-card">
          <div className="brand">
            <span className="brand-mark">GV</span>
            <span>
              <strong>Glimbr Voice</strong>
              <small>Admin</small>
            </span>
          </div>
          <div className="banner neutral">Checking session...</div>
        </div>
      </main>
    );
  }

  if (!authSession) {
    return (
      <main className="auth-shell">
        <div className="auth-card">
          <a className="brand" href="/">
            <span className="brand-mark">GV</span>
            <span>
              <strong>Glimbr Voice</strong>
              <small>Admin</small>
            </span>
          </a>
          {error && (
            <div className="banner error">
              <AlertTriangle size={18} />
              <span>{error}</span>
            </div>
          )}
          {notice && (
            <div className="banner success">
              <CheckCircle2 size={18} />
              <span>{notice}</span>
            </div>
          )}
          <AuthOrgProvisioningPanel onSessionChange={handleSessionChange} />
        </div>
      </main>
    );
  }

  return (
    <main className="admin-shell">
      <aside className="sidebar">
        <a className="brand" href="/">
          <span className="brand-mark">GV</span>
          <span>
            <strong>Glimbr Voice</strong>
            <small>Admin</small>
          </span>
        </a>
        <nav aria-label="Admin sections">
          {navGroups.map((group) => (
            <div className="nav-group" key={group}>
              <span>{group}</span>
              {navItems.filter((item) => item.group === group).map((item) => {
                const Icon = item.icon;
                return (
                  <button
                    type="button"
                    key={item.id}
                    className={activeSection === item.id ? "active" : undefined}
                    onClick={() => setActiveSection(item.id)}
                  >
                    <Icon size={18} />
                    {item.label}
                  </button>
                );
              })}
            </div>
          ))}
        </nav>
      </aside>

      <section className="workspace">
        <header className="workspace-header">
          <div>
            <p className="workspace-kicker">Standalone customer portal</p>
            <h1>
              <ActiveIcon size={26} />
              {navItems.find((item) => item.id === activeSection)?.label || "Overview"}
            </h1>
          </div>
          <div className="header-controls">
            <label>
              Organization
              <select
                value={authSession.activeOrg.orgId}
                onChange={(event) => void switchHeaderOrg(event.target.value)}
                disabled={authSession.memberships.length < 2}
              >
                {authSession.memberships.map((membership) => (
                  <option value={membership.orgId} key={membership.orgId}>
                    {membership.orgName}
                  </option>
                ))}
              </select>
            </label>
            <button className="icon-button" type="button" onClick={() => void loadData()} disabled={loading}>
              <RefreshCcw size={17} />
              Refresh
            </button>
          </div>
        </header>

        {error && (
          <div className="banner error">
            <AlertTriangle size={18} />
            <span>{error}</span>
          </div>
        )}
        {notice && (
          <div className="banner success">
            <CheckCircle2 size={18} />
            <span>{notice}</span>
          </div>
        )}

        {activeSection === "dashboard" && (
          <DashboardPage data={data} metrics={metrics} loading={loading} />
        )}
        {activeSection === "assistant-defaults" && (
          <AssistantDefaultsPage
            orgId={orgId}
            accounts={data.accounts}
            onNotice={setNotice}
            onError={setError}
            onUpdated={() => void loadData()}
          />
        )}
        {activeSection === "account" && (
          <AccountPage
            orgId={orgId}
            session={authSession}
            accounts={data.accounts}
            onNotice={setNotice}
            onError={setError}
            onUpdated={() => void loadData()}
          />
        )}
        {activeSection === "billing" && <BillingPage />}
        {activeSection === "settings" && (
          <SettingsPage
            session={authSession}
            onSessionChange={handleSessionChange}
          />
        )}
      </section>
    </main>
  );
}

function DashboardPage(props: { data: AdminData; metrics: Array<{ label: string; value: string; tone: string }>; loading: boolean }) {
  const { data, metrics, loading } = props;
  const assistantDefaults = assistantDefaultsFromAccount(data.accounts[0]);
  const engine = findVoiceEngineOption(assistantDefaults.engineId);
  const voice = findVoiceOption(assistantDefaults.voiceId);

  return (
    <section className="page-stack">
      <section className="page-intro">
        <Activity size={22} />
        <div>
          <h2>Status at a glance</h2>
          <p>High-level call status, default voice setup, and billing readiness for this organization.</p>
        </div>
      </section>
      <section className="status-grid">
        <article className="runtime-card">
          <div className="card-heading">
            <Headphones size={22} />
            <div>
              <h2>Voice service</h2>
              <p>Customer call handling status.</p>
            </div>
          </div>
          <dl>
            <div>
              <dt>Status</dt>
              <dd><StatusPill value={loading ? "loading" : data.health?.status || "unknown"} /></dd>
            </div>
            <div>
              <dt>Calls in progress</dt>
              <dd>{String(data.health?.active_calls ?? 0)}</dd>
            </div>
            <div>
              <dt>Default voice</dt>
              <dd>{voice.name}</dd>
            </div>
            <div>
              <dt>Voice engine</dt>
              <dd>{engine.tier}</dd>
            </div>
          </dl>
        </article>

        <article className="runtime-card">
          <div className="card-heading">
            <CreditCard size={22} />
            <div>
              <h2>Account status</h2>
              <p>Usage rates and account access for this customer portal.</p>
            </div>
          </div>
          <dl>
            <div>
              <dt>Suggested inbound</dt>
              <dd>{formatCustomerRate(engine.suggestedInboundPrice)}/min</dd>
            </div>
            <div>
              <dt>AU local outbound</dt>
              <dd>{formatCustomerRate(engine.suggestedLocalOutboundPrice)}/min</dd>
            </div>
            <div>
              <dt>AU mobile outbound</dt>
              <dd>{formatCustomerRate(engine.suggestedMobileOutboundPrice)}/min</dd>
            </div>
          </dl>
        </article>
      </section>

      <section className="metric-grid" aria-label="Workspace metrics">
        {metrics.map((metric) => (
          <article className={`metric ${metric.tone}`} key={metric.label}>
            <span>{metric.label}</span>
            <strong>{metric.value}</strong>
          </article>
        ))}
      </section>

      <section className="panel">
        <div className="panel-header">
          <div>
            <h2>Customer setup</h2>
            <p>Manage the defaults that shape new voice assistants and customer call pricing.</p>
          </div>
        </div>
        <div className="resource-list">
          <ResourceRow label="Default voice" value={voice.name} />
          <ResourceRow label="Default engine" value={engine.tier} />
          <ResourceRow label="Billing model" value="Usage based" />
          <ResourceRow label="Call records" value={assistantDefaults.saveTranscripts ? "Transcripts on" : "Transcripts off"} />
        </div>
      </section>
    </section>
  );
}

function AccountPage(props: {
  orgId: string;
  session: AdminAuthSession;
  accounts: VoiceRecord[];
  onNotice: (message: string | null) => void;
  onError: (message: string | null) => void;
  onUpdated: () => void;
}) {
  const [tab, setTab] = useState("overview");
  const membershipRows: VoiceRecord[] = props.session.memberships.map((membership) => ({
    ...membership,
    status: "active"
  }));

  return (
    <section className="page-stack">
      <section className="page-intro">
        <Building2 size={22} />
        <div>
          <h2>Account</h2>
          <p>Manage the organization and people who can administer this Glimbr Voice account.</p>
        </div>
      </section>
      <PageTabs
        active={tab}
        onChange={setTab}
        tabs={[
          { id: "overview", label: "Overview", icon: Building2 },
          { id: "members", label: "Members", icon: UserRound }
        ]}
      />
      {tab === "overview" && (
        <section className="account-overview">
          <section className="status-grid">
            <article className="panel">
              <div className="card-heading">
                <Building2 size={21} />
                <div>
                  <h2>Active organization</h2>
                  <p>This is the organization currently being managed.</p>
                </div>
              </div>
              <CodeList
                rows={[
                  ["Organization", props.session.activeOrg.orgName],
                  ["Organization ID", props.orgId],
                  ["Role", props.session.activeOrg.role],
                  ["Signed-in user", props.session.user.email]
                ]}
              />
            </article>
            <DataPanel
              title="Account profile"
              description="Business profile data linked to this Glimbr Voice organization."
              empty="No account profile is available yet."
              rows={props.accounts}
              columns={[
                { label: "Name", render: (row) => asText(row.name || row.displayName || row.display_name || row.id) },
                { label: "Organization", render: (row) => asText(row.orgName || row.org_name || row.orgId || row.org_id || props.orgId) },
                { label: "Source", render: (row) => asText(row.sourceService || row.source_service || row.source) },
                { label: "Updated", render: (row) => formatDate(row.updatedAt || row.updated_at || row.createdAt || row.created_at) }
              ]}
            />
          </section>
        </section>
      )}
      {tab === "members" && (
        <DataPanel
          title="Members"
          description="People who can administer this Glimbr Voice organization."
          empty="No active memberships found."
          rows={membershipRows}
          columns={[
            { label: "Organization", render: (row) => asText(row.orgName) },
            { label: "Organization ID", render: (row) => asText(row.orgId) },
            { label: "Role", render: (row) => asText(row.role) },
            { label: "Status", render: (row) => <StatusPill value={asText(row.status)} /> }
          ]}
        />
      )}
    </section>
  );
}

function BillingPage() {
  const [tab, setTab] = useState("usage");

  return (
    <section className="section-stack">
      <section className="page-intro">
        <CreditCard size={22} />
        <div>
          <h2>Usage billing</h2>
          <p>Track usage charges, minute-based costs, and downloadable billing records.</p>
        </div>
      </section>
      <PageTabs
        active={tab}
        onChange={setTab}
        tabs={[
          { id: "usage", label: "Usage", icon: CreditCard },
          { id: "ledger", label: "Cost Ledger", icon: ListChecks },
          { id: "exports", label: "Exports", icon: FileText }
        ]}
      />
      {tab === "usage" && <BillingPanel />}
      {tab === "ledger" && (
        <article className="panel">
          <div className="card-heading">
            <ListChecks size={21} />
            <div>
              <h2>Cost ledger</h2>
              <p>Shows how voice minutes are charged for this account.</p>
            </div>
          </div>
          <CodeList
            rows={[
              ["Billing model", "Usage-based per completed voice minute"],
              ["Voice engine", "Included in the selected rate card"],
              ["Monthly plan", "None"],
              ["Monthly minimum", "None"],
              ["Payment collection", "Coming soon"]
            ]}
          />
        </article>
      )}
      {tab === "exports" && (
        <article className="panel">
          <div className="card-heading">
            <FileText size={21} />
            <div>
              <h2>Usage exports</h2>
              <p>Download usage and billing records when exports are available.</p>
            </div>
          </div>
          <EmptyState message="Usage exports are not available yet." />
        </article>
      )}
    </section>
  );
}

function AssistantDefaultsPage(props: {
  orgId: string;
  accounts: VoiceRecord[];
  onNotice: (message: string | null) => void;
  onError: (message: string | null) => void;
  onUpdated: () => void;
}) {
  const [assistantDefaults, setAssistantDefaults] = useState<AssistantDefaultsDraft>(() => assistantDefaultsFromAccount(props.accounts[0]));
  const [savingAssistantDefaults, setSavingAssistantDefaults] = useState(false);
  const activeAccount = props.accounts[0] || null;
  const selectedEngine = findVoiceEngineOption(assistantDefaults.engineId);
  const selectedVoice = findVoiceOption(assistantDefaults.voiceId);

  useEffect(() => {
    setAssistantDefaults(assistantDefaultsFromAccount(props.accounts[0]));
  }, [props.accounts]);

  async function saveAssistantDefaults() {
    const accountId = stringValue(activeAccount?.id) || props.orgId;
    const metadata = objectValue(activeAccount?.metadata);
    const engineMetadata = voiceEngineMetadata(selectedEngine);
    const defaultAssistant = {
      name: assistantDefaults.name,
      firstMessage: assistantDefaults.firstMessage,
      first_message: assistantDefaults.firstMessage,
      systemPrompt: assistantDefaults.systemPrompt,
      system_prompt: assistantDefaults.systemPrompt,
      language: assistantDefaults.language,
      voice: {
        voiceId: assistantDefaults.voiceId,
        voice_id: assistantDefaults.voiceId,
        displayName: selectedVoice.name,
        display_name: selectedVoice.name
      },
      engine: engineMetadata,
      voiceEngine: engineMetadata,
      voice_engine: engineMetadata,
      recordCalls: assistantDefaults.recordCalls,
      record_calls: assistantDefaults.recordCalls,
      saveTranscripts: assistantDefaults.saveTranscripts,
      save_transcripts: assistantDefaults.saveTranscripts
    };

    setSavingAssistantDefaults(true);
    props.onError(null);
    props.onNotice(null);

    try {
      await voiceRequest<VoiceRecord>(`/account/${encodeURIComponent(accountId)}`, {
        orgId: props.orgId,
        method: "PATCH",
        body: {
          metadata: {
            ...metadata,
            defaultAssistant,
            default_assistant: defaultAssistant,
            voiceEngine: engineMetadata,
            voice_engine: engineMetadata,
            realtimeModel: selectedEngine.internalModel,
            realtime_model: selectedEngine.internalModel,
            realtimeReasoningTier: selectedEngine.id,
            realtime_reasoning_tier: selectedEngine.id,
            realtimeReasoningEffort: selectedEngine.reasoningEffort,
            realtime_reasoning_effort: selectedEngine.reasoningEffort,
            realtime: engineMetadata
          }
        }
      });
      props.onNotice("Assistant defaults saved");
      props.onUpdated();
    } catch (err) {
      props.onError(err instanceof Error ? err.message : String(err));
    } finally {
      setSavingAssistantDefaults(false);
    }
  }

  return (
    <section className="section-stack">
      <section className="page-intro">
        <Bot size={22} />
        <div>
          <h2>Assistant defaults</h2>
          <p>Choose the default voice, engine, rate profile, greeting, and call record settings for new assistants.</p>
        </div>
      </section>
      <AssistantDefaultsPanel
        value={assistantDefaults}
        selectedEngine={selectedEngine}
        selectedVoice={selectedVoice}
        saving={savingAssistantDefaults}
        onChange={setAssistantDefaults}
        onSave={() => void saveAssistantDefaults()}
        onPlaySample={playVoiceSample}
      />
    </section>
  );
}

function SettingsPage(props: {
  session: AdminAuthSession;
  onSessionChange: (session: AdminAuthSession | null) => void;
}) {
  const [tab, setTab] = useState("auth");

  return (
    <section className="section-stack">
      <section className="page-intro">
        <Settings size={22} />
        <div>
          <h2>Settings</h2>
          <p>Manage your profile and security settings for this organization.</p>
        </div>
      </section>
      <PageTabs
        active={tab}
        onChange={setTab}
        tabs={[
          { id: "auth", label: "Profile", icon: UserRound },
          { id: "security", label: "Security", icon: ShieldCheck }
        ]}
      />
      {tab === "auth" && <AuthOrgProvisioningPanel session={props.session} onSessionChange={props.onSessionChange} />}
      {tab === "security" && (
        <article className="panel">
          <div className="card-heading">
            <ShieldCheck size={21} />
            <div>
              <h2>Account security</h2>
              <p>Access to this workspace is limited to confirmed members of the organization.</p>
            </div>
          </div>
          <CodeList
            rows={[
              ["Signed-in user", props.session.user.email],
              ["Email confirmed", props.session.user.emailConfirmed ? "Yes" : "No"],
              ["Organization", props.session.activeOrg.orgName],
              ["Role", props.session.activeOrg.role],
              ["Session", "Active"]
            ]}
          />
        </article>
      )}
    </section>
  );
}

function AssistantDefaultsPanel(props: {
  value: AssistantDefaultsDraft;
  selectedEngine: VoiceEngineOption;
  selectedVoice: typeof voiceOptions[number];
  saving: boolean;
  onChange: (next: AssistantDefaultsDraft) => void;
  onSave: () => void;
  onPlaySample: (voice: typeof voiceOptions[number]) => void;
}) {
  const { value } = props;

  function update<K extends keyof AssistantDefaultsDraft>(key: K, nextValue: AssistantDefaultsDraft[K]) {
    props.onChange({ ...value, [key]: nextValue });
  }

  return (
    <section className="assistant-defaults-grid">
      <article className="panel assistant-defaults-panel">
        <div className="panel-header">
          <div>
            <h2>Default voice</h2>
            <p>Pick the voice customers hear when a new assistant is created.</p>
          </div>
          <span>{props.selectedVoice.name}</span>
        </div>
        <div className="voice-choice-grid" role="radiogroup" aria-label="Default voice">
          {voiceOptions.map((voice) => (
            <article className={value.voiceId === voice.id ? "voice-card active" : "voice-card"} key={voice.id}>
              <button
                type="button"
                role="radio"
                aria-checked={value.voiceId === voice.id}
                onClick={() => update("voiceId", voice.id)}
              >
                <span>
                  <strong>{voice.name}</strong>
                  <small>{voice.tone}</small>
                </span>
              </button>
              <button className="inline-action" type="button" onClick={() => props.onPlaySample(voice)}>
                <Play size={15} />
                Play sample
              </button>
            </article>
          ))}
        </div>
      </article>

      <article className="panel assistant-defaults-panel">
        <div className="panel-header">
          <div>
            <h2>Voice engine</h2>
            <p>Select the default engine and customer-facing per-minute rates.</p>
          </div>
          <span>{props.selectedEngine.tier}</span>
        </div>
        <EngineRateTable selectedEngine={props.selectedEngine} onSelect={(engineId) => update("engineId", engineId)} />
      </article>

      <article className="panel assistant-defaults-panel">
        <div className="panel-header">
          <div>
            <h2>Selected rate summary</h2>
            <p>Rates shown are the suggested customer prices for this engine.</p>
          </div>
          <CreditCard size={21} />
        </div>
        <div className="rate-summary-grid">
          <RateSummary label="Inbound" value={props.selectedEngine.suggestedInboundPrice} />
          <RateSummary label="Inbound toll-free" value={props.selectedEngine.suggestedInboundPrice + 0.05} />
          <RateSummary label="AU local outbound" value={props.selectedEngine.suggestedLocalOutboundPrice} />
          <RateSummary label="AU mobile outbound" value={props.selectedEngine.suggestedMobileOutboundPrice} />
        </div>
        <div className="campaign-pricing">
          <ResourceRow label="Reserved outbound concurrency" value={`${formatMinuteRate(OUTBOUND_CAMPAIGN_PRICING.reservedConcurrencyRate)} / line / reserved min`} />
          <ResourceRow label="Minimum reservation" value={`${OUTBOUND_CAMPAIGN_PRICING.minimumReservationMinutes} minutes`} />
          <ResourceRow label="PSTN transfers" value="Add second outbound leg" />
        </div>
      </article>

      <article className="panel assistant-defaults-panel">
        <div className="panel-header">
          <div>
            <h2>Conversation defaults</h2>
            <p>Set the default greeting and behavior used for new assistants.</p>
          </div>
          <MessageSquareText size={21} />
        </div>
        <div className="field-grid">
          <label>
            Assistant name
            <input value={value.name} onChange={(event) => update("name", event.target.value)} />
          </label>
          <label>
            Language
            <input value={value.language} onChange={(event) => update("language", event.target.value)} />
          </label>
        </div>
        <label>
          First message
          <textarea rows={3} value={value.firstMessage} onChange={(event) => update("firstMessage", event.target.value)} />
        </label>
        <label>
          Default instructions
          <textarea rows={8} value={value.systemPrompt} onChange={(event) => update("systemPrompt", event.target.value)} />
        </label>
      </article>

      <article className="panel assistant-defaults-panel">
        <div className="panel-header">
          <div>
            <h2>Call artifacts</h2>
            <p>Set the default capture behavior for call records created by assistant workflows.</p>
          </div>
          <MessageSquareText size={21} />
        </div>
        <label className="checkbox-row">
          <input
            type="checkbox"
            checked={value.recordCalls}
            onChange={(event) => update("recordCalls", event.target.checked)}
          />
          Record calls by default
        </label>
        <label className="checkbox-row">
          <input
            type="checkbox"
            checked={value.saveTranscripts}
            onChange={(event) => update("saveTranscripts", event.target.checked)}
          />
          Save transcripts by default
        </label>
      </article>

      <article className="panel assistant-defaults-panel">
        <div className="panel-header">
          <div>
            <h2>Save defaults</h2>
            <p>Persist these defaults to the organization account metadata.</p>
          </div>
          <Mic2 size={21} />
        </div>
        <div className="panel-actions">
          <CodeList rows={[
            ["Voice", props.selectedVoice.name],
            ["Engine", props.selectedEngine.tier],
            ["Inbound", `${formatCustomerRate(props.selectedEngine.suggestedInboundPrice)} / min`]
          ]} />
          <button className="primary-button" type="button" onClick={props.onSave} disabled={props.saving}>
            {props.saving ? "Saving..." : "Save assistant defaults"}
          </button>
        </div>
      </article>
    </section>
  );
}

function EngineRateTable(props: {
  selectedEngine: VoiceEngineOption;
  onSelect: (engineId: VoiceEngineId) => void;
}) {
  return (
    <div className="table-wrap compact-table">
      <table>
        <thead>
          <tr>
            <th>Tier</th>
            <th>Reasoning</th>
            <th>Est. AI cost/min</th>
            <th>Inbound cost</th>
            <th>Suggested inbound price</th>
            <th>AU local outbound cost</th>
            <th>Suggested local outbound</th>
            <th>AU mobile outbound cost</th>
            <th>Suggested mobile outbound</th>
          </tr>
        </thead>
        <tbody>
          {VOICE_ENGINE_OPTIONS.map((engine) => (
            <tr className={props.selectedEngine.id === engine.id ? "selected-row" : undefined} key={engine.id}>
              <td>
                <button
                  className="table-radio"
                  type="button"
                  role="radio"
                  aria-checked={props.selectedEngine.id === engine.id}
                  onClick={() => props.onSelect(engine.id)}
                >
                  {engine.tier}
                  {engine.id === DEFAULT_VOICE_ENGINE_ID && <span>Recommended</span>}
                </button>
              </td>
              <td>{engine.reasoning}</td>
              <td>{formatCostRate(engine.estimatedAiCostPerMinute)}</td>
              <td>{formatCostRate(engine.inboundCost)}</td>
              <td>{formatCustomerRate(engine.suggestedInboundPrice)}</td>
              <td>{formatCostRate(engine.auLocalOutboundCost)}</td>
              <td>{formatCustomerRate(engine.suggestedLocalOutboundPrice)}</td>
              <td>{formatCostRate(engine.auMobileOutboundCost)}</td>
              <td>{formatCustomerRate(engine.suggestedMobileOutboundPrice)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function RateSummary({ label, value }: { label: string; value: number }) {
  return (
    <div className="rate-summary">
      <span>{label}</span>
      <strong>{formatCustomerRate(value)}</strong>
      <small>/ min</small>
    </div>
  );
}

function PageTabs(props: {
  active: string;
  tabs: Array<{ id: string; label: string; icon: typeof Activity }>;
  onChange: (id: string) => void;
}) {
  return (
    <nav className="page-tabs" aria-label="Page sections">
      {props.tabs.map((tab) => {
        const Icon = tab.icon;
        return (
          <button
            type="button"
            key={tab.id}
            className={props.active === tab.id ? "active" : undefined}
            onClick={() => props.onChange(tab.id)}
          >
            <Icon size={16} />
            {tab.label}
          </button>
        );
      })}
    </nav>
  );
}

function DataPanel(props: {
  title: string;
  description: string;
  empty: string;
  rows: VoiceRecord[];
  columns: Array<{ label: string; render: (row: VoiceRecord) => ReactNode }>;
}) {
  return (
    <section className="panel table-panel">
      <div className="panel-header">
        <div>
          <h2>{props.title}</h2>
          <p>{props.description}</p>
        </div>
        <span>{props.rows.length} rows</span>
      </div>
      {props.rows.length === 0 ? (
        <EmptyState message={props.empty} />
      ) : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                {props.columns.map((column) => (
                  <th key={column.label}>{column.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {props.rows.map((row, index) => (
                <tr key={asText(row.id, `row-${index}`)}>
                  {props.columns.map((column) => (
                    <td key={column.label}>{column.render(row)}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function EmptyState({ message }: { message: string }) {
  return (
    <div className="empty-state">
      <Database size={22} />
      <span>{message}</span>
    </div>
  );
}

function ResourceRow({ label, value }: { label: string; value: number | string }) {
  return (
    <div>
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function StatusPill({ value }: { value: string }) {
  const normalized = value.toLowerCase();
  const tone = normalized.includes("ok") || normalized.includes("active") || normalized.includes("configured")
    ? "good"
    : normalized.includes("error") || normalized.includes("failed") || normalized.includes("disabled")
      ? "bad"
      : "warn";
  return <span className={`status-pill ${tone}`}>{value}</span>;
}

function CodeList({ rows }: { rows: Array<[string, string]> }) {
  return (
    <dl className="code-list">
      {rows.map(([label, value]) => (
        <div key={label}>
          <dt>{label}</dt>
          <dd>{value}</dd>
        </div>
      ))}
    </dl>
  );
}

function voiceEngineFromAccount(account: VoiceRecord | undefined): VoiceEngineId {
  const metadata = objectValue(account?.metadata);
  const defaultAssistant = objectValue(metadata.defaultAssistant || metadata.default_assistant);
  const defaultEngine = objectValue(defaultAssistant.voiceEngine || defaultAssistant.voice_engine || defaultAssistant.engine);
  const metadataEngine = objectValue(metadata.voiceEngine || metadata.voice_engine);
  const realtime = objectValue(metadata.realtime || metadata.realtimeConfig || metadata.realtime_config);
  const engineId = stringValue(defaultEngine.id)
    || stringValue(defaultEngine.tier)
    || stringValue(metadataEngine.id)
    || stringValue(metadataEngine.tier)
    || stringValue(metadata.realtimeReasoningTier)
    || stringValue(metadata.realtime_reasoning_tier)
    || stringValue(realtime.tier);
  if (engineId === "economy" || engineId === "standard" || engineId === "basic" || engineId === "better" || engineId === "complex" || engineId === "max") {
    return engineId;
  }

  const model = stringValue(defaultEngine.model)
    || stringValue(metadataEngine.model)
    || stringValue(metadata.realtimeModel)
    || stringValue(metadata.realtime_model)
    || stringValue(realtime.model);
  if (model === "realtime-mini") return "economy";

  const effort = stringValue(defaultEngine.reasoningEffort)
    || stringValue(defaultEngine.reasoning_effort)
    || stringValue(metadataEngine.reasoningEffort)
    || stringValue(metadataEngine.reasoning_effort)
    || stringValue(metadata.realtimeReasoningEffort)
    || stringValue(metadata.realtime_reasoning_effort)
    || stringValue(realtime.reasoningEffort)
    || stringValue(realtime.reasoning_effort);
  if (effort === "minimal") return "basic";
  if (effort === "medium") return "better";
  if (effort === "high") return "complex";
  if (effort === "xhigh") return "max";
  return DEFAULT_VOICE_ENGINE_ID;
}

function assistantDefaultsFromAccount(account: VoiceRecord | undefined): AssistantDefaultsDraft {
  const metadata = objectValue(account?.metadata);
  const defaultAssistant = objectValue(metadata.defaultAssistant || metadata.default_assistant);
  const voice = objectValue(defaultAssistant.voice || defaultAssistant.voiceConfig || defaultAssistant.voice_config);

  return {
    name: stringValue(defaultAssistant.name) || defaultAssistantDefaults.name,
    firstMessage: stringValue(defaultAssistant.firstMessage)
      || stringValue(defaultAssistant.first_message)
      || defaultAssistantDefaults.firstMessage,
    systemPrompt: stringValue(defaultAssistant.systemPrompt)
      || stringValue(defaultAssistant.system_prompt)
      || stringValue(defaultAssistant.instructions)
      || defaultAssistantDefaults.systemPrompt,
    voiceId: findVoiceOption(stringValue(voice.voiceId)
      || stringValue(voice.voice_id)
      || stringValue(defaultAssistant.voiceId)
      || stringValue(defaultAssistant.voice_id)
      || defaultAssistantDefaults.voiceId).id,
    language: stringValue(defaultAssistant.language) || defaultAssistantDefaults.language,
    engineId: voiceEngineFromAccount(account),
    recordCalls: booleanValue(defaultAssistant.recordCalls ?? defaultAssistant.record_calls, defaultAssistantDefaults.recordCalls),
    saveTranscripts: booleanValue(defaultAssistant.saveTranscripts ?? defaultAssistant.save_transcripts, defaultAssistantDefaults.saveTranscripts)
  };
}

function findVoiceOption(id: unknown): typeof voiceOptions[number] {
  const normalized = typeof id === "string" ? id : "";
  return voiceOptions.find((voice) => voice.id === normalized) || voiceOptions[0];
}

function voiceEngineMetadata(engine: VoiceEngineOption): VoiceRecord {
  const metadata: VoiceRecord = {
    id: engine.id,
    tier: engine.tier,
    reasoning: engine.reasoning,
    model: engine.internalModel,
    pricing: {
      estimatedAiCostPerMinute: engine.estimatedAiCostPerMinute,
      inboundCost: engine.inboundCost,
      suggestedInboundPrice: engine.suggestedInboundPrice,
      auLocalOutboundCost: engine.auLocalOutboundCost,
      suggestedLocalOutboundPrice: engine.suggestedLocalOutboundPrice,
      auMobileOutboundCost: engine.auMobileOutboundCost,
      suggestedMobileOutboundPrice: engine.suggestedMobileOutboundPrice
    }
  };
  if (engine.reasoningEffort) {
    metadata.reasoningEffort = engine.reasoningEffort;
    metadata.reasoning_effort = engine.reasoningEffort;
  }
  return metadata;
}

function playVoiceSample(voice: typeof voiceOptions[number]) {
  if (typeof window === "undefined" || !("speechSynthesis" in window)) return;
  window.speechSynthesis.cancel();
  const utterance = new SpeechSynthesisUtterance(voice.sample);
  utterance.rate = voice.id === "harper" ? 1.05 : 0.96;
  utterance.pitch = voice.id === "miles" ? 0.86 : voice.id === "harper" ? 1.08 : 1;
  window.speechSynthesis.speak(utterance);
}

function objectValue(value: unknown): VoiceRecord {
  return value && typeof value === "object" && !Array.isArray(value) ? value as VoiceRecord : {};
}

function booleanValue(value: unknown, fallback: boolean): boolean {
  return typeof value === "boolean" ? value : fallback;
}

function stringValue(value: unknown): string | null {
  return typeof value === "string" && value.trim() ? value.trim() : null;
}

function valueOrNull<T>(result: PromiseSettledResult<unknown>): T | null {
  return result.status === "fulfilled" ? result.value as T : null;
}

function valueOrEmpty(result: PromiseSettledResult<unknown>): unknown {
  return result.status === "fulfilled" ? result.value : {};
}
